import Network from 'lib/api/network';

export default {
  namespaced: true,

  state() {
    return {
      questions: null,
      primarySubmissions: null,
      nonPrimarySubmissions: null,
      primaryRoleName: null,
      nonPrimaryRoleName: null,
      tooltips: {},
      idForSubmissionWithOpenMatchingMenu: null,
      responsesTableEl: null,
      pendingMatches: {},
      proposing: false,
      activeAttemptId: 0,
      canEdit: false,
    };
  },

  getters: {
    usingMatching(state) {
      return !!state.nonPrimarySubmissions;
    },

    numColumns(state, getters) {
      let num = state.questions.length + 1; // 1 column for every question, plus 1 for 'Participant'
      if (getters.usingMatching) num += 1; // Add another column for 'Role' if using matching
      return num;
    },

    tableWidthStyle(state) {
      if (state.responsesTableEl) {
        const tablePosition = state.responsesTableEl.getBoundingClientRect();
        const tableWidth = tablePosition.right - tablePosition.left;
        // We only want to stretch it if its over the width of the rest of 'main', which is 960px (see layout.scss)
        // Were we not to do this, the actions buttons would be floated all the way to the right even with a small table
        if (tableWidth > 960) {
          const screenWidth = window.innerWidth;
          const extendedTableWidth = screenWidth - tablePosition.left;
          return { width: `${extendedTableWidth}px` };
        }
      }
    },
  },

  mutations: {
    establishTooltip(state, { ref, refName, value }) {
      if (ref && ref.offsetWidth < ref.scrollWidth) {
        state.tooltips[refName] = value;
      }
    },

    matchSubmissions(state, { primaryId, nonPrimaryId, reason = null, score = null }) {
      // nonPrimaryId can be null, in the case of breaking matches
      const primary = state.primarySubmissions.find(sub => sub.id === primaryId);
      primary.matched_submission_id = nonPrimaryId;
      primary.ai_reason = reason;
      primary.ai_score = score;
      state.pendingMatches[primaryId] = {
        matched_submission_id: nonPrimaryId,
        session_id: primary.session_ids[0], // It's safe to assume that primary submissions will only ever be attached to 1 session
        reason, // persisted on the session when saved (AI matches only)
        score,
      };
    },

    clearPendingMatches(state) {
      state.pendingMatches = {};
    },

    setProposing(state, value) {
      state.proposing = value;
    },

    // Marks a fresh start, so anything left over from before knows it's old news.
    startNewAttempt(state) {
      state.activeAttemptId += 1;
    },
  },

  actions: {
    proposeMatches({ commit, dispatch, state }, { triggerUrl, pollUrl, rematch = false, adminContext = null }) {
      commit('startNewAttempt');
      const attemptId = state.activeAttemptId;
      const isStale = () => state.activeAttemptId !== attemptId;

      commit('setProposing', true);

      const loadMatches = (matches) => {
        (matches || []).forEach((match) => {
          // Don't let a stale proposal overwrite an already-saved match. Unsaved ones (in
          // pendingMatches) stay overwritable so rematch still works.
          const primary = state.primarySubmissions.find(sub => sub.id === match.primary_submission_id);
          if (primary && primary.matched_submission_id && !state.pendingMatches[match.primary_submission_id]) return;
          commit('matchSubmissions', {
            primaryId: match.primary_submission_id,
            nonPrimaryId: match.matched_submission_id,
            reason: match.reason,
            score: match.score,
          });
        });
        commit('setProposing', false);
      };

      const fail = () => {
        dispatch('setErrorToast', null, { root: true });
        commit('setProposing', false);
      };

      const MAX_POLLS = 60; // ~2 minutes at 2s each, then give up
      let polls = 0;
      const poll = () => {
        if (isStale()) return;
        if (polls >= MAX_POLLS) { fail(); return; }
        polls += 1;
        Network.get(pollUrl, {
          success: (data) => {
            if (isStale()) return;
            if (data.status === 'ready') loadMatches(data.matches);
            else if (data.status === 'error') fail();
            else setTimeout(poll, 2000);
          },
          error: () => { if (!isStale()) fail(); },
        });
      };

      Network.post(triggerUrl, { rematch, admin_context: adminContext }, {
        success: (data) => {
          if (isStale()) return;
          if (data.status === 'ready') loadMatches(data.matches);
          else if (data.status === 'error') fail();
          else poll();
        },
        error: () => { if (!isStale()) fail(); },
      });
    },

    // Stops watching only. The job (if running) keeps going and updates the cache.
    cancelMatching({ commit }) {
      commit('startNewAttempt');
      commit('setProposing', false);
    },

    approveMatches({ state, commit, dispatch }, { url }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      return new Promise((_resolve) => {
        const matches = Object.keys(state.pendingMatches).map((primarySubmissionId) => ({ primary_submission_id: primarySubmissionId, ...state.pendingMatches[primarySubmissionId] }));
        Network.put(
          url,
          { matches },
          {
            success: () => {
              dispatch('setSuccessToast', 'Saved matches!', { root: true });
              commit('clearPendingMatches');
            },
            error: () => {
              dispatch('setErrorToast', null, { root: true });
            },
            complete: () => {
              commit('update', { key: 'formSubmitted', value: false }, { root: true });
            },
          },
        );
      });
    },
  },
};
